import api from '../../api/client';
import React, { useState, useEffect } from "react";
import {
  Modal,
  Form,
  Input,
  Select,
  Button,
  Space,
  message,
  Spin,
  Row,
  Col,
  Typography,
  Tag,
  Divider,
  Descriptions,
} from "antd";
import { DollarOutlined, BankOutlined, MobileOutlined } from "@ant-design/icons";
import axios from "axios";
import { useSelector } from "react-redux";
import config from "../../config";

const { Text } = Typography;
const { Option } = Select;
const { TextArea } = Input;

const PaymentConfirmationModal = ({ visible, onCancel, onConfirm, transaction }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [details, setDetails] = useState(null);
  const [chartOfAccounts, setChartOfAccounts] = useState([]);
  const [coaOptions, setCoaOptions] = useState([]);
  const account = useSelector((state) => state.account);
  const environment = window.location.hostname === "localhost" ? "development" : "production";
  const apiUrl = config[environment].apiUrl;

  // Fetch chart of accounts
  useEffect(() => {
    const fetchChartOfAccounts = async () => {
      try {
        const { data } = await api.get(`/api/chart-of-accounts`);
        setChartOfAccounts(data);
        const options = data.map((acc) => ({
          value: acc.gl_code,
          label: `${acc.gl_code} - ${acc.account_name}`,
        }));
        setCoaOptions(options);
      } catch (err) {
        console.error("Failed to fetch chart of accounts", err);
      }
    };
    fetchChartOfAccounts();
  }, [apiUrl]);

  useEffect(() => {
    if (visible && transaction) {
      fetchTransactionDetails();
    }
  }, [visible, transaction]);

  const fetchTransactionDetails = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(
        `/api/transactions/details/${transaction.transactionRecordId || transaction.id}`
      );
      setDetails(data);
      form.setFieldsValue({
        transaction_code: data.transaction_code,
        description: data.description || data.transactionDescription,
        remarks: data.remarks,
        amount: data.amount || data.toPay,
        paidAmount: data.paidAmount,
        stakeholderId: data.stakeholderId,
        transaction_date: data.transaction_date?.slice(0, 10),
        paymentStatus: data.paymentStatus,
        paymentMethod: data.paymentMethod,
        gl_debit: data.gl_debit,
        gl_credit: data.gl_credit,
      });
    } catch (err) {
      message.error("Failed to load transaction details");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdate = async (values) => {
    setLoading(true);
    try {
      await api.post(`/api/transactions/update/${details.id}`, {
        ...values,
        userId: account.user.id,
        userName: account.user.firstname,
      });
      message.success("Transaction updated successfully");
      setEditMode(false);
      fetchTransactionDetails(); // Refresh details
      if (onConfirm) onConfirm();
    } catch (err) {
      message.error("Failed to update transaction");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const getStatusTag = (status) => {
    const color = status === "fully_paid" ? "green" : status === "partially_paid" ? "orange" : "red";
    const text = status === "fully_paid" ? "Fully Paid" : status === "partially_paid" ? "Partially Paid" : "Unpaid";
    return <Tag color={color}>{text}</Tag>;
  };

  const getAccountName = (glCode) => {
    const account = chartOfAccounts.find((acc) => acc.gl_code === glCode);
    return account ? `${glCode} - ${account.account_name}` : glCode || "—";
  };

  if (!transaction) return null;

  const balance = (details?.amount || 0) - (details?.paidAmount || 0);

  return (
    <Modal
      title="Transaction Details"
      open={visible}
      onCancel={() => {
        setEditMode(false);
        onCancel();
      }}
      width={750}
      footer={
        <Space>
          <Button onClick={onCancel}>Close</Button>
          {editMode && (
            <Button type="primary" onClick={() => form.submit()} loading={loading}>
              Save Changes
            </Button>
          )}
          {!editMode && (
            <Button onClick={() => setEditMode(true)}>Edit</Button>
          )}
        </Space>
      }
      destroyOnClose
    >
      <Spin spinning={loading}>
        {details && (
          <>
            {/* Transaction Summary */}
            <div
              style={{
                background: "#f5f5f5",
                padding: 16,
                borderRadius: 8,
                marginBottom: 16,
              }}
            >
              <Row gutter={16}>
                <Col span={12}>
                  <Text type="secondary">Transaction Code</Text>
                  <div>
                    <Text strong>{details.transaction_code}</Text>
                  </div>
                </Col>
                <Col span={12}>
                  <Text type="secondary">Date</Text>
                  <div>
                    <Text strong>
                      {new Date(details.transaction_date).toLocaleDateString()}
                    </Text>
                  </div>
                </Col>
                <Col span={12} style={{ marginTop: 8 }}>
                  <Text type="secondary">Amount</Text>
                  <div>
                    <Text strong style={{ fontSize: 16, color: "#1677ff" }}>
                      KES {details.amount?.toLocaleString()}
                    </Text>
                  </div>
                </Col>
                <Col span={12} style={{ marginTop: 8 }}>
                  <Text type="secondary">Status</Text>
                  <div>{getStatusTag(details.paymentStatus)}</div>
                </Col>
                <Col span={12} style={{ marginTop: 8 }}>
                  <Text type="secondary">Paid Amount</Text>
                  <div>
                    <Text strong style={{ color: "#52c41a" }}>
                      KES {details.paidAmount?.toLocaleString()}
                    </Text>
                  </div>
                </Col>
                <Col span={12} style={{ marginTop: 8 }}>
                  <Text type="secondary">Balance</Text>
                  <div>
                    <Text strong style={{ color: balance > 0 ? "#faad14" : "#52c41a" }}>
                      KES {balance.toLocaleString()}
                    </Text>
                  </div>
                </Col>
              </Row>
            </div>

            {editMode ? (
              <Form form={form} layout="vertical" onFinish={handleUpdate}>
                <Form.Item name="description" label="Description">
                  <TextArea rows={2} />
                </Form.Item>
                <Form.Item name="remarks" label="Notes">
                  <TextArea rows={2} />
                </Form.Item>
                <Row gutter={16}>
                  <Col span={12}>
                    <Form.Item name="transaction_date" label="Transaction Date">
                      <Input type="date" />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item name="paymentMethod" label="Payment Method">
                      <Select>
                        <Option value="cash">Cash</Option>
                        <Option value="bank_transfer">Bank Transfer</Option>
                        <Option value="mpesa">M-Pesa</Option>
                        <Option value="cheque">Cheque</Option>
                      </Select>
                    </Form.Item>
                  </Col>
                </Row>
                <Row gutter={16}>
                  <Col span={12}>
                    <Form.Item name="gl_debit" label="GL Debit Account">
                      <Select
                        showSearch
                        optionFilterProp="children"
                        placeholder="Select Debit Account"
                      >
                        {coaOptions.map((opt) => (
                          <Option key={opt.value} value={opt.value}>
                            {opt.label}
                          </Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item name="gl_credit" label="GL Credit Account">
                      <Select
                        showSearch
                        optionFilterProp="children"
                        placeholder="Select Credit Account"
                      >
                        {coaOptions.map((opt) => (
                          <Option key={opt.value} value={opt.value}>
                            {opt.label}
                          </Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Col>
                </Row>
                <Form.Item name="paymentStatus" label="Payment Status">
                  <Select>
                    <Option value="unpaid">Unpaid</Option>
                    <Option value="partially_paid">Partially Paid</Option>
                    <Option value="fully_paid">Fully Paid</Option>
                  </Select>
                </Form.Item>
              </Form>
            ) : (
              <>
                <Divider>Transaction Details</Divider>
                <Descriptions column={1} bordered size="small">
                  <Descriptions.Item label="Description">
                    {details.description || details.transactionDescription || "—"}
                  </Descriptions.Item>
                  <Descriptions.Item label="Notes">
                    {details.remarks || "—"}
                  </Descriptions.Item>
                  <Descriptions.Item label="Payment Method">
                    {details.paymentMethod ? details.paymentMethod.charAt(0).toUpperCase() + details.paymentMethod.slice(1).replace("_", " ") : "—"}
                  </Descriptions.Item>
                  <Descriptions.Item label="GL Debit Account">
                    <Tag color="blue">{getAccountName(details.gl_debit)}</Tag>
                  </Descriptions.Item>
                  <Descriptions.Item label="GL Credit Account">
                    <Tag color="green">{getAccountName(details.gl_credit)}</Tag>
                  </Descriptions.Item>
                </Descriptions>
              </>
            )}
          </>
        )}
      </Spin>
    </Modal>
  );
};

export default PaymentConfirmationModal;